import React, { MouseEvent, useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography,
  styled,
  IconButton,
  Divider,
  Button,
  Alert,
  Select,
  MenuItem,
  TextField,
  InputLabel,
} from "@mui/material";
import { IWithClassName } from "../../theme/default.theme";
import { ReactComponent as PlayStore } from "../../svg/play_store.svg";
import { ReactComponent as AppStore } from "../../svg/app-store.svg";
import SearchIcon from "@mui/icons-material/Search";
import { IRootState, useAppDispatch, useAppSelector } from "../../redux/store";
import { addAppManulThunk, searchAppThunk } from "../../redux/thunks/app.thunk";
import { IAppInfo } from "../../interfaces/dto/app.dto";
import AppMenu from "./menu";
import { AppType } from "../../enum/app";
import { getCountriesThunk } from "../../redux/thunks/global.thunk";
import { createSelector } from "@reduxjs/toolkit";
import { FileUploader } from "react-drag-drop-files";
// import AppTile from "./user-app";

interface IProps extends IWithClassName {
  children?: React.ReactNode;
  title?: string;
  paper?: boolean;
}

const selector = createSelector(
    (state: IRootState) => state.app, 
    ({global}: IRootState) => global.countries,
    (app, countries) => ({
      search: app.search,
      countries: countries.data ?? [],
    }));

const AddApp = ({ className, children, title, paper }: IProps) => {
  const { search: searchOptions, countries } = useAppSelector(selector);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [appType, setAppType] = useState<AppType>(AppType.Android);
  const [appManualForm, setAppManualForm] = useState<{
    name?: string;
    url?: string;
    thubnail?: File;
  }>({});
  const [search, setSearch] = useState<string>("");
  const [gl, setGl] = useState<string>("");
  const [searchMode, setSearchMode] = useState<string>("q");
  const [options, setOptions] = useState<Array<IAppInfo>>([]);
  const [showManual, setShowManual] = useState<boolean>(false);
  const [appAlert, setAppAlert] = useState<{
    open: boolean;
    isSuccess: boolean;
  }>({
    open: false,
    isSuccess: false,
  });
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getCountriesThunk());
  }, [dispatch])

  useEffect(() => {
    setTimeout(() => {
      setAppAlert({ open: false, isSuccess: false });
    }, 10000);
  }, [options]);

  useEffect(() => {
    setOptions(searchOptions.data ?? []);
  }, [searchOptions]);

  const handleSearch = async ({
    currentTarget,
  }: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(currentTarget.parentElement);
    if(search.length){
      await dispatch(searchAppThunk(search, appType, searchMode, gl));
    }
  };

  const handleChange = (label: string) => ({target}: React.ChangeEvent<HTMLInputElement>) => 
    setAppManualForm((manulFrom) => ({...manulFrom, [label]: target.value}))
  
  const handleFileChange = (files: FileList) => {
    if(files){
      setAppManualForm((manulFrom) => ({...manulFrom, thubnail: files[0]}));
    }
  }

  const submitManulAppFrom = () => {
    if(appManualForm.name && appManualForm.thubnail && appManualForm.url) {
      dispatch(addAppManulThunk({
        name: appManualForm.name,
        url: appManualForm.url,
        thumbnail: appManualForm.thubnail,
      }, appType));

      setAppManualForm({});
      setShowManual(false);
    }
  }

  return (
    <Box component={paper ? Paper : undefined} className={className}>
      <Box className="form-container">
        {title && <Typography component="h3">{title}</Typography>}
        {appAlert.open && (
          <Alert severity={appAlert.isSuccess ? "success" : "error"}>
            {" "}
            Application added Successfully
          </Alert>
        )}
        <RadioGroup
          row
          className="store-selector"
          onChange={({ currentTarget }) =>
            setAppType(currentTarget.value as AppType)
          }
          value={appType}
        >
          <FormControlLabel
            value={AppType.Android}
            control={<Radio icon={<PlayStore />} checkedIcon={<PlayStore />} />}
            label="Play Store"
          />
          <FormControlLabel
            value={AppType.IOS}
            control={<Radio icon={<AppStore />} checkedIcon={<AppStore />} />}
            label="App Store"
          />
        </RadioGroup>
        <FormControl>
          <Paper component="form" className="search-container">
            <TextField
              placeholder="Search Apps"
              className="search-input"
              variant="outlined"
              onChange={({ currentTarget }) => setSearch(currentTarget.value)}
            />
            <FormControl>
              <InputLabel htmlFor="multi-select">GEO</InputLabel>
              <Select 
                variant="outlined" 
                className="gl-input"
                placeholder="GEO"
                value={gl}
                onChange={({target}) => setGl(target.value)}
                renderValue={(idData: string) => countries.find(({id}) => id === idData)?.id ?? ""}>
                {countries.map(({id, name}) => (
                  <MenuItem key={id} value={id}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Select variant="outlined" value={searchMode} onChange={({target}) => setSearchMode(target.value)}>
              <MenuItem value="q">General search</MenuItem>
              <MenuItem value="product_id">search by product id</MenuItem>
            </Select>
            <IconButton
              type="button"
              className="search-button"
              aria-label="search"
              onClick={handleSearch}
            >
              <SearchIcon />
            </IconButton>
            <Divider className="divider" orientation="vertical" />
            <Button variant="outlined" onClick={handleSearch}>
              Search
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              className="add-app-manul-cont"
              onClick={() => setShowManual((show) => !show)}>
              Add App Manually 
            </Button>
          </Paper>
          <AppMenu
            anchorEl={anchorEl}
            options={options}
            setAnchorEl={setAnchorEl}
            appType={appType}
            setAppAlert={setAppAlert}
          />
        </FormControl>
      </Box>

      {showManual && (
        <Box className="add-manual-app">
          <FormControl>
            <TextField label="App Name" variant="outlined" onChange={handleChange("name")} value={appManualForm?.name ?? ""}/>
          </FormControl>
          <FormControl>
            <TextField label="App URL" variant="outlined" type="url" onChange={handleChange("url")} value={appManualForm?.url ?? ""} />
          </FormControl>
          <FormControl>
            <FileUploader
              multiple={true}
              value={appManualForm?.thubnail ?? []}
              handleChange={handleFileChange}
              name="file"
              types={["JPEG", "PNG", "JPG", "SVG"]} />
          </FormControl>
          <FormControl className="save-button-cont">
            <Button variant="contained" color="primary" onClick={submitManulAppFrom}>
              Add App
            </Button>
          </FormControl>
        </Box>
      )}            
      
      {children}
    </Box>
  );
};

export default styled(AddApp)(({ theme }) => ({
  ".gl-input": {
    width: 80,
    margin: theme.spacing(0, 1),
  },
  ".search-button": {
    padding: 10,
  },
  ".divider": {
    height: 28,
    margin: theme.spacing(0.5),
  },
  ".search-input": {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
  h3: {
    fontWeight: 600,
    fontSize: 16,
  },
  ".store-selector": {
    display: "flex",
    gap: 10,
    ".MuiFormControlLabel-root": {
      padding: theme.spacing(1, 2),
    },
  },
  ".form-container": {
    width: "50%",
    minWidth: 300,
    padding: theme.spacing(2),
    display: "grid",
    gap: theme.spacing(2),
  },
  ".search-container": {
    padding: theme.spacing(0.25, 0.5),
    display: "flex",
    alignItems: "center",
    width: 800,
  },
  ".add-manual-app": {
    display: "grid",
    gap: theme.spacing(2),
    maxWidth: 500,
    padding: theme.spacing(2),
    ".MuiFormControl-root": {
      width: "100%",
    },
    ".save-button-cont": {
      float: "right",
    },
  },
  ".add-app-manul-cont": {
    margin: theme.spacing(0, 1),
  }
}));
